import { GlobalContext } from "components/GlobalContext";
import { createFilterableArrayColumnDefinition, IColumnDefinition } from "components/SortableTable";
import IEquipment, { IEquipmentCreateOrUpdate, IEquipmentFilters, IEquipmentInitResult } from "models/sql/IEquipment";
import { useContext, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import EquipmentService from "services/EquipmentService";
import { formatDate } from "utilities/StringUtility";
import { applyGlobalFilterData } from "utilities/MiscUtilities";
import CommonLookupDisplay from "components/CommonLookupDisplay";
import { LinkContainer } from "react-router-bootstrap";
import { getStatus, getStatusDate } from "utilities/EquipmentUtility";

interface IDemosLookupProps {
    baseData?: IEquipmentInitResult;
}

const COUNTRIES_FILTER_KEY = "countries";
const STATES_FILTER_KEY = "states";

const DemosLookup = (props: IDemosLookupProps) => {
    const [filter, setFilter] = useState<IEquipmentFilters>({});
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const navigate = useNavigate();

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        EquipmentService.DownloadExcelFileAsync(
            accessToken,
            "DemoExport.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const getColumns = (): IColumnDefinition<IEquipment, IEquipmentFilters>[] => {
        return [
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "departmentName",
                "departmentNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`departmentName${row.equipmentId}`}>
                    {row.fireDepartment?.fireDeptName ?? row.departmentName}</td>,
                "Department"),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                // @ts-ignore
                "repCompanies",
                "repCompanies",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`company${row.equipmentId}`}>
                    {row.salesRep?.dealer?.dealerName ?? row.dealer?.dealerName ?? row.salesRep?.company ?? ""}</td>,
                "Rep Company"),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                // @ts-ignore
                "repNames",
                "repNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`company${row.equipmentId}`}>
                    {row.salesRep?.fullName ?? ""}</td>,
                "Rep Name"),
            {
                propName: "demoRequestDate",
                title: "Requested",
                customRenderer: (value, data) => <td className="align-top" key={`Requested${data.equipmentId}`}>
                    {data?.demoRequestDate ? formatDate(new Date(data?.demoRequestDate)) : ""}
                </td>,
                sortable: true
            },
            {
                propName: "demoApprovalDate",
                title: "Approved",
                customRenderer: (value, data) => <td className="align-top" key={`Approved${data.equipmentId}`}>
                    {data?.demoApprovalDate ? formatDate(new Date(data?.demoApprovalDate)) : ""}
                </td>,
                sortable: true
            },
            {
                propName: "shippedDate",
                title: "Shipped",
                customRenderer: (value, data) => <td className="align-top" key={`Shipped${data.equipmentId}`}>
                    {data?.shippedDate ? formatDate(new Date(data?.shippedDate)) : ""}
                </td>,
                sortable: true
            },
            {
                propName: "demoStartDate",
                title: "Started",
                customRenderer: (value, data) => <td className="align-top" key={`Started${data.equipmentId}`}>
                    {data?.demoStartDate ? formatDate(new Date(data?.demoStartDate)) : ""}
                </td>,
                sortable: true
            },
            {
                propName: "demoEndDate",
                title: "Ended",
                customRenderer: (value, data) => <td className="align-top" key={`Ended${data.equipmentId}`}>
                    {data?.demoEndDate ? formatDate(new Date(data?.demoEndDate)) : ""}
                </td>,
                sortable: true
            },
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                // @ts-ignore
                "state",
                "states",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`state${row.equipmentId}`}>
                    {row.fireDepartment?.hqstate ?? ""}</td>,
                "State"),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "country",
                "countries",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`country${row.equipmentId}`}>
                    {row.fireDepartment?.hqCountry ?? row.country}</td>,
                "Country")
        ];
    };

    const rowClassRenderer = (row: IEquipment) => {
        return (row.demoApprovalDate ? "" : "notApproved");
    }

    const getTools = () => [
        <>
            {
                globalContext.roles.some(r => r === "Web.Tools") &&
                <LinkContainer to="/Tools/EquipmentMassAdd">
                    <Button
                        style={{
                            "display": "inline",
                            "width": "130px",
                            "position": "relative",
                            "left": "-8px"
                        }}
                        variant="primary"
                        className="ms-2 float-start full-sm"
                        onClick={() => {
                        }}>Mass Add</Button>
                </LinkContainer>
            }
        </>,
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Admin") &&
                <Button
                    className="me-2 full-sm"
                    key="createnew"
                    variant="success"
                    onClick={() => { navigate(`/Demos/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<IEquipmentFilters, IEquipment, IEquipmentCreateOrUpdate>
            baseFilters={{
                isDemo: true,
                sortBys: ["EquipmentId"],
                sortDescending: true
            }}
            filterHints={{
                equipmentIds: "Array",
                dealerIds: "Array",
                departmentIds: "Array",
                departmentNames: "Array",
                stationIds: "Array",
                productIds: "Array",
                parentEquipmentIds: "Array",
                serialNumbers: "Array",
                extraData: "Array",
                sortBys: "Array",
                productPrefixs: "Array",
                minScore: "Number",
                maxScore: "Number",
                sortDescending: "Boolean",
                states: "Array",
                countries: "Array",
                repCompanies: "Array",
                registrationNames: "Array",
                registrationEmails: "Array",
                organizationTypes: "Array",
                stationNumbers: "Array",
                locations: "Array"
            }}
            dataTypeName="Demo"
            dataTypeNamePlural="Demos"
            pathName="Demos"
            primaryIdKey="equipmentId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={EquipmentService}
            tools={getTools()}
            rowClassRenderer={rowClassRenderer}
        />
    );
}

export default DemosLookup;
